import React from "react";
import { CCBadge } from "@pymblecitizencoders/cc-ui";
import { Clock, LucideIcon } from "lucide-react";
import "./eventManager.css";

interface EventModalHeadProps {
	/** Must match the CCModal `title` — see below on why it is repeated. */
	title: string;
	icon: LucideIcon;
	status: { tone: "warning" | "success"; text: string };
}

/**
 * Modal header: icon, title and status chip on one line.
 *
 * cc-ui's CCModal types `title` as a string and points the dialog's
 * `aria-labelledby` at the `<h2>` it renders from it, so an icon and a badge
 * cannot be passed through it. Casting JSX in would work at runtime — the
 * component renders `title` as children — but it would break the typed contract
 * and fold the badge text into the dialog's accessible name.
 *
 * So the real `<h2>` stays, carrying the accessible name, and is hidden
 * visually by `.em-modal-head`'s CSS; this row is what you see. The title text
 * here is `aria-hidden` because the `<h2>` already provides it — without that a
 * screen reader would meet the same words twice. The status chip is NOT hidden:
 * it is information, not decoration.
 */
export const EventModalHead: React.FC<EventModalHeadProps> = ({
	title,
	icon: Icon,
	status,
}) => (
	<div className="em-modal-head">
		<span className="em-modal-head__icon" aria-hidden="true">
			<Icon size={26} />
		</span>
		<span className="em-modal-head__title" aria-hidden="true">
			{title}
		</span>
		<CCBadge tone={status.tone}>
			<Clock size={14} aria-hidden="true" /> {status.text}
		</CCBadge>
	</div>
);
